import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import ImageLightbox from '../components/shared/ImageLightbox';
import { toast } from 'sonner';
import {
  Search, FileText, FileImage, FileVideo, File as FileIcon, Download,
  ExternalLink, FolderOpen, RefreshCw, X, Ticket, MessageSquare, CheckSquare,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const toAbs = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

const TYPE_LABELS = { image: 'Imagini', video: 'Video', document: 'Documente' };
const SOURCE_LABELS = { ticket: 'Tichete', task: 'Task-uri', comment: 'Comentarii' };
const SOURCE_ICON = { ticket: Ticket, task: CheckSquare, comment: MessageSquare };

const iconFor = (type, contentType) => {
  if (type === 'image' || (contentType || '').startsWith('image/')) return FileImage;
  if (type === 'video' || (contentType || '').startsWith('video/')) return FileVideo;
  if ((contentType || '').includes('pdf')) return FileText;
  return FileIcon;
};

const fmtBytes = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

const DocumentsLibraryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counters, setCounters] = useState({ by_type: {}, by_source: {} });
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [uploaderFilter, setUploaderFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 300, offset: 0 };
      if (search.trim()) params.search = search.trim();
      if (typeFilter !== 'all') params.type = typeFilter;
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (uploaderFilter.trim()) params.uploader = uploaderFilter.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/documents-library', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setCounters(res.data.counters || { by_type: {}, by_source: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la incarcare');
    } finally { setLoading(false); }
  }, [search, typeFilter, sourceFilter, uploaderFilter, dateFrom, dateTo]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  const clearFilters = () => {
    setSearch(''); setTypeFilter('all'); setSourceFilter('all');
    setUploaderFilter(''); setDateFrom(''); setDateTo('');
  };

  // Pre-compute image-only list for lightbox navigation
  const imageItems = useMemo(
    () => items.filter(i => i.type === 'image'),
    [items]
  );

  const openLightboxFor = (item) => {
    const idx = imageItems.findIndex(x => x.url === item.url);
    setLightboxIndex(idx >= 0 ? idx : 0);
  };

  const goToSource = (item) => {
    if (item.source_type === 'ticket') navigate(`/tickets/${item.source_id}`);
    else if (item.source_type === 'task' || item.source_type === 'comment') navigate(`/cross-dept-tasks/${item.source_id}`);
  };

  const hasActiveFilter = search || typeFilter !== 'all' || sourceFilter !== 'all' || uploaderFilter || dateFrom || dateTo;

  return (
    <div className="space-y-4 md:space-y-6 pb-24" data-testid="documents-library-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FolderOpen className="w-7 h-7 text-indigo-600" />
            Biblioteca Documentelor
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Cauta in toate atasamentele (tichete, task-uri, comentarii) — fotografii, PDF-uri, documente tehnice.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          data-testid="library-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Reimprospateaza
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        <KpiCard label="Total fisiere" value={total} color="text-slate-700" />
        <KpiCard label="Imagini" value={counters.by_type?.image || 0} color="text-emerald-600" />
        <KpiCard label="Documente" value={counters.by_type?.document || 0} color="text-indigo-600" />
        <KpiCard label="Video" value={counters.by_type?.video || 0} color="text-purple-600" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cauta dupa nume fisier..."
                className="pl-10 h-10"
                data-testid="library-search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-10" data-testid="library-type-filter"><SelectValue placeholder="Tip" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate tipurile</SelectItem>
                <SelectItem value="image">Imagini</SelectItem>
                <SelectItem value="document">Documente</SelectItem>
                <SelectItem value="video">Video</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="h-10" data-testid="library-source-filter"><SelectValue placeholder="Sursa" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate sursele</SelectItem>
                <SelectItem value="ticket">Tichete</SelectItem>
                <SelectItem value="task">Task-uri interdep.</SelectItem>
                <SelectItem value="comment">Comentarii</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={uploaderFilter}
              onChange={(e) => setUploaderFilter(e.target.value)}
              placeholder="Autor..."
              className="h-10"
              data-testid="library-uploader-filter"
            />
            <div className="flex gap-2">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10" data-testid="library-date-from" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <div className="flex gap-2 items-center">
              <span className="text-xs text-slate-500">Pana la:</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-44" data-testid="library-date-to" />
            </div>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-rose-600 hover:bg-rose-50"
                data-testid="library-clear-filters"
              >
                <X className="w-4 h-4 mr-1" /> Sterge filtrele
              </Button>
            )}
            <span className="ml-auto text-xs text-slate-500 tabular-nums">{total} rezultat(e)</span>
          </div>
        </CardContent>
      </Card>

      {/* Grid of results */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="font-medium">Nicio potrivire</p>
          <p className="text-xs mt-1">Incearca sa modifici filtrele sau termenul de cautare.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {items.map((item, idx) => (
            <DocumentCard
              key={`${item.url}-${idx}`}
              item={item}
              onOpen={() => (item.type === 'image' ? openLightboxFor(item) : window.open(toAbs(item.url), '_blank'))}
              onNavigate={() => goToSource(item)}
            />
          ))}
        </div>
      )}

      {lightboxIndex !== null && imageItems.length > 0 && (
        <ImageLightbox
          images={imageItems}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  );
};

const KpiCard = ({ label, value, color }) => (
  <Card className="bg-white">
    <CardContent className="p-3 md:p-4">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className={`text-xl md:text-3xl font-extrabold tabular-nums mt-1 ${color || 'text-slate-700'}`}>{value}</p>
    </CardContent>
  </Card>
);

const DocumentCard = ({ item, onOpen, onNavigate }) => {
  const Icon = iconFor(item.type, item.content_type);
  const isImage = item.type === 'image';
  const SrcIcon = SOURCE_ICON[item.source_type] || FileIcon;
  return (
    <Card
      className="overflow-hidden hover:shadow-md transition-shadow border border-slate-200"
      data-testid={`library-card-${item.id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="block w-full h-28 md:h-32 bg-slate-100 overflow-hidden relative group"
        title={isImage ? 'Deschide preview' : 'Deschide fisier'}
        data-testid={`library-open-${item.id}`}
      >
        {isImage ? (
          <img
            src={toAbs(item.url)}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-12 h-12 text-indigo-400" />
          </div>
        )}
        <span className={`absolute top-1.5 left-1.5 text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${
          isImage ? 'bg-emerald-100 text-emerald-700' : item.type === 'video' ? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'
        }`}>
          {(item.content_type || '').split('/').pop()?.toUpperCase() || item.type}
        </span>
      </button>
      <CardContent className="p-2.5 space-y-1.5">
        <p className="text-xs font-semibold text-slate-800 truncate" title={item.name}>{item.name}</p>
        <div className="flex items-center gap-1 text-[10px] text-slate-500">
          <SrcIcon className="w-3 h-3 flex-shrink-0" />
          <span className="truncate" title={item.source_label}>{item.source_label}</span>
        </div>
        <div className="flex items-center justify-between text-[10px] text-slate-400">
          <span className="truncate">{item.uploaded_by_name || '-'}</span>
          <span className="tabular-nums">{fmtBytes(item.size)}</span>
        </div>
        <p className="text-[10px] text-slate-400">{fmtDate(item.uploaded_at)}</p>
        <div className="flex gap-1 pt-1">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[10px] px-2 flex-1"
            onClick={onNavigate}
            data-testid={`library-goto-${item.id}`}
          >
            <ExternalLink className="w-3 h-3 mr-1" /> Sursa
          </Button>
          <a
            href={toAbs(item.url)}
            download={item.name}
            className="h-7 w-7 flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
            title="Descarca"
            data-testid={`library-download-${item.id}`}
          >
            <Download className="w-3 h-3" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

// eslint-disable-next-line no-unused-vars
const _unused = { TYPE_LABELS, SOURCE_LABELS };

export default DocumentsLibraryPage;
